/**
 * 请求加密和响应解密处理器
 * 将加密解密逻辑从 request.js 中抽取出来，提高代码可维护性
 */

import { encrypt, decrypt, CryptoType, hybridEncrypt } from '@/utils/crypto-util'
import { shouldEncrypt, getEncryptConfig, getRsaPublicKeyByUrl } from '@/utils/encrypt-config'
import { stringifySafe, parseSafe, convertBigIntToString } from '@/utils/json-bigint'
import { RSADECRY } from '@/utils/customDecryption'
import CryptoJS from 'crypto-js'
import qs from 'qs'

/**
 * 混合解密（使用公钥）：使用 customDecryption.js 中的公钥解密方法
 * 先使用RSA公钥解密AES密钥，再使用AES密钥解密数据
 *
 * @param {Object|String} encryptedContent - 加密内容（JSON对象或字符串）
 * @param {String} rsaPublicKey - RSA公钥（Base64编码或PEM格式）
 * @returns {String} 解密后的原始数据
 */
function hybridDecryptWithPublicKeyUsingCustomDecryption(encryptedContent, rsaPublicKey) {
  try {
    // 第一步：解析加密数据（可能是JSON字符串或对象）
    let encryptedObj
    if (typeof encryptedContent === 'string') {
      encryptedObj = JSON.parse(encryptedContent)
    } else {
      encryptedObj = encryptedContent
    }

    // 验证加密对象结构
    if (!encryptedObj || !encryptedObj.encryptedKey || !encryptedObj.encryptedData) {
      throw new Error('加密数据格式错误，缺少 encryptedKey 或 encryptedData')
    }

    // 第二步：使用RSA公钥解密AES密钥（使用 customDecryption.js 的方法）
    // 注意：后端返回的 encryptedKey 是Base64编码的，需要先处理特殊字符
    const encryptedKey = encryptedObj.encryptedKey.replace(/~SLASH~/g, '/').replace(/~SUB~/g, '+')

    const aesKeyBase64 = RSADECRY.decryptByPublicKey(encryptedKey, rsaPublicKey)
    console.log('---------')
    console.log(aesKeyBase64)
    // 验证解密结果
    if (!aesKeyBase64 || aesKeyBase64 === false || typeof aesKeyBase64 !== 'string') {
      console.log('解密失败')
      throw new Error('RSA公钥解密AES密钥失败，返回值为: ' + typeof aesKeyBase64)
    }

    // 清理密钥字符串，移除所有非Base64字符
    const cleanedKey = aesKeyBase64.trim().replace(/[^A-Za-z0-9+/=]/g, '')

    // 第三步：将Base64密钥解码为WordArray用于AES解密
    let aesKeyWordArray
    try {
      // 确保Base64字符串长度是4的倍数（Base64要求）
      const paddingNeeded = (4 - (cleanedKey.length % 4)) % 4
      const paddedKey = cleanedKey + '='.repeat(paddingNeeded)

      aesKeyWordArray = CryptoJS.enc.Base64.parse(paddedKey)

      // 验证AES密钥长度（应该是16字节 = 128位）
      if (aesKeyWordArray.sigBytes !== 16) {
        if (aesKeyWordArray.sigBytes > 16) {
          // 如果长度超过16字节，截取前16字节
          const keyHex = CryptoJS.enc.Hex.stringify(aesKeyWordArray)
          const truncatedHex = keyHex.substring(0, 32) // 16字节 = 32个十六进制字符
          aesKeyWordArray = CryptoJS.enc.Hex.parse(truncatedHex)
        } else {
          throw new Error('AES密钥长度不正确，期望16字节，实际' + aesKeyWordArray.sigBytes + '字节')
        }
      }
    } catch (parseError) {
      throw new Error('AES密钥Base64解析失败: ' + parseError.message)
    }

    // 第四步：恢复特殊字符
    const encryptedData = String(encryptedObj.encryptedData).replace(/~SLASH~/g, '/').replace(/~SUB~/g, '+')

    // 第五步：Base64解码加密数据
    const encryptedDataBytes = CryptoJS.enc.Base64.parse(encryptedData)

    // 第六步：创建加密对象
    const encrypted = CryptoJS.lib.CipherParams.create({
      ciphertext: encryptedDataBytes
    })

    // 第七步：使用AES密钥解密数据
    const decrypted = CryptoJS.AES.decrypt(
      encrypted,
      aesKeyWordArray,
      {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7
      }
    )

    // 检查解密结果是否有效
    if (!decrypted || decrypted.sigBytes === 0) {
      throw new Error('AES解密失败：解密结果为空或无效')
    }

    // 第八步：转换为UTF-8字符串
    let decryptedData
    try {
      decryptedData = decrypted.toString(CryptoJS.enc.Utf8)
    } catch (utf8Error) {
      throw new Error('AES解密失败：解密后的数据不是有效的UTF-8格式。错误详情: ' + utf8Error.message)
    }

    // 验证解密后的数据是否为空
    if (!decryptedData || decryptedData.length === 0) {
      throw new Error('AES解密后数据为空')
    }

    return decryptedData
  } catch (error) {
    throw new Error('混合解密（公钥）失败: ' + error.message)
  }
}

/**
 * 处理请求加密
 * @param {Object} config - axios 请求配置对象
 * @returns {Object} 处理后的请求配置对象
 */
export function handleRequestEncryption(config) {
  const encryptConfig = getEncryptConfig()
  console.log('是否加密')
  console.log(encryptConfig)
  const method = (config.method || 'get').toLowerCase()
  const needEncrypt = encryptConfig.enabled && shouldEncrypt(config.url)

  // 如果加密未启用，直接返回
  if (!needEncrypt) {
    return config
  }

  // 处理GET请求的params参数加密
  if (method === 'get') {
    // 检查URL中是否已经包含查询参数（直接在URL中拼接的情况）
    const urlHasParams = config.url && config.url.includes('?')
    const hasParamsObj = config.params && Object.keys(config.params).length > 0

    // 如果URL中已有参数或config.params中有参数，都需要处理
    if (urlHasParams || hasParamsObj) {
      let paramsToEncrypt = {}

      // 如果有config.params，使用config.params（先转换大整数为字符串）
      if (hasParamsObj) {
        paramsToEncrypt = convertBigIntToString({ ...config.params })
      }

      // 如果URL中也有参数，解析出来合并
      if (urlHasParams) {
        const urlParts = config.url.split('?')
        const baseUrl = urlParts[0]
        const urlParams = urlParts[1]

        // 解析URL中的查询参数
        if (urlParams) {
          const urlParamPairs = urlParams.split('&')
          urlParamPairs.forEach(pair => {
            const equalIndex = pair.indexOf('=')
            if (equalIndex > 0) {
              const key = pair.substring(0, equalIndex)
              let value = pair.substring(equalIndex + 1)
              if (key) {
                value = value ? decodeURIComponent(value) : ''
                // 如果值看起来像大整数（长度超过15位的纯数字），保持为字符串
                if (value && /^-?\d{16,}$/.test(value)) {
                  paramsToEncrypt[key] = value // 保持为字符串，避免精度丢失
                } else {
                  paramsToEncrypt[key] = value
                }
              }
            } else if (pair) {
              // 没有值的参数
              paramsToEncrypt[pair] = ''
            }
          })
        }
        // 移除URL中的查询参数部分
        config.url = baseUrl
      }

      // 如果有参数需要加密
      if (Object.keys(paramsToEncrypt).length > 0) {
        if (needEncrypt) {
          try {
            // 将params转换为JSON字符串并加密（使用大整数安全的序列化）
            // 先转换大整数为字符串，再序列化
            const safeParams = convertBigIntToString(paramsToEncrypt)
            const paramsStr = stringifySafe(safeParams)

            // 根据加密类型选择加密方式
            let encryptedParams = null

            if (encryptConfig.cryptoType === 'HYBRID') {
              // 混合加密：使用RSA公钥加密AES密钥，使用AES密钥加密数据
              const rsaPublicKey = getRsaPublicKeyByUrl(config.url)
              if (!rsaPublicKey) {
                encryptedParams = null
              } else {
                encryptedParams = hybridEncrypt(paramsStr, rsaPublicKey)
              }
            } else if (encryptConfig.cryptoType === 'RSA') {
              // RSA加密：需要根据URL选择对应的RSA公钥
              const rsaPublicKey = getRsaPublicKeyByUrl(config.url)
              if (!rsaPublicKey) {
                encryptedParams = null
              } else {
                encryptedParams = encrypt(paramsStr, CryptoType.RSA, rsaPublicKey)
              }
            } else {
              // AES加密：使用AES密钥
              encryptedParams = encrypt(paramsStr, CryptoType.AES, encryptConfig.encryptKey)
            }

            if (encryptedParams) {
              // 将加密后的参数作为 encryptedParams 放在URL中
              config.params = {
                encryptedParams: encryptedParams
              }
            } else {
              config.params = paramsToEncrypt
            }
          } catch (error) {
            config.params = paramsToEncrypt
          }
        } else {
          // 不需要加密，保持原样
          config.params = paramsToEncrypt
        }
      }
  }

    // 将params转换为URL查询字符串（无论是否加密）
    if (config.params && Object.keys(config.params).length > 0) {
    const url = config.url + '?' + qs.stringify(config.params, { indices: false })
    config.params = {}
    config.url = url
    }
  }

  // 处理POST/PUT/DELETE/PATCH等请求的data加密（所有请求方式都要加密）
  // 注意：FormData（文件上传）不应该被加密，应该跳过
  if (needEncrypt && (method === 'post' || method === 'put' || method === 'delete' || method === 'patch')) {
    try {
      if (config.data) {
        // 检查是否是 FormData（文件上传），如果是则跳过加密
        const isFormData = config.data instanceof FormData
        if (isFormData) {
          // FormData 请求不加密，直接跳过
          // 确保 Content-Type 让浏览器自动设置（删除默认的 application/json）
          delete config.headers['Content-Type']
          delete config.headers['content-type']
          return config
        }

        // 检查 Content-Type，如果没有设置，默认为 application/json
        const contentType = config.headers['Content-Type'] || config.headers['content-type'] || 'application/json'
        // 只对 application/json 类型的请求进行加密
        if (contentType.includes('application/json')) {
          // 使用大整数安全的序列化（将大整数转换为字符串）
          let dataStr
          if (typeof config.data === 'string') {
            dataStr = config.data
          } else {
            const safeData = convertBigIntToString(config.data)
            dataStr = stringifySafe(safeData)
          }

          // 加密数据
          let encryptedData = null

          if (encryptConfig.cryptoType === 'HYBRID') {
            // 混合加密：使用RSA公钥加密AES密钥，使用AES密钥加密数据
            const rsaPublicKey = getRsaPublicKeyByUrl(config.url)
            if (!rsaPublicKey) {
              encryptedData = null
            } else {
              try {
                encryptedData = hybridEncrypt(dataStr, rsaPublicKey)
              } catch (error) {
                // 加密失败，保持 encryptedData 为 null
              }
            }
          } else {
            // 传统加密方式（AES或RSA）
          const cryptoType = encryptConfig.cryptoType === 'RSA' ? CryptoType.RSA : CryptoType.AES
            encryptedData = encrypt(dataStr, cryptoType, encryptConfig.encryptKey)
          }

          if (encryptedData) {
            // 将加密后的数据设置为字符串
            config.data = encryptedData
          }
        }
      }
    } catch (error) {
      // 加密失败，使用原始数据
    }
  }

  return config
}

/**
 * 处理响应解密
 * @param {Object} res - axios 响应对象
 * @returns {*} 解密后的响应数据
 */
export function handleResponseDecryption(res) {
  const requestUrl = res.config.url
  const encryptConfig = getEncryptConfig()

  // 如果是 blob 或 arraybuffer 响应，直接返回，不进行任何处理
  if (res.config.responseType === 'blob' || res.config.responseType === 'arraybuffer') {
    return res
  }

  // 加解密处理：对所有JSON响应数据进行解密（除了排除路径）
  let responseData = res.data

  // 如果响应数据已经是对象（axios自动解析的JSON），先处理大整数精度问题
  // 注意：如果响应是加密的字符串，这里res.data还是字符串，不会进入这个处理
  if (responseData && typeof responseData === 'object' && !Array.isArray(responseData)) {
    // 检查是否是加密的响应（如果是加密响应，会在下面处理）
    // 这里只处理非加密的JSON响应
    const isJsonResponse = res.headers['content-type'] && res.headers['content-type'].includes('application/json')
    const isEncrypted = typeof res.data === 'string' &&
                       (res.data.includes('~SLASH~') || res.data.includes('~SUB~') ||
                        (res.data.length > 50 && /^[A-Za-z0-9+/=~_-]+$/.test(res.data)))

    // 如果不是加密响应，且是JSON响应，处理大整数
    if (isJsonResponse && !isEncrypted) {
      responseData = convertBigIntToString(responseData)
    }
  }
  const isLoginRequest = requestUrl.includes('/login')

  // 检查是否需要解密（启用加密且路径需要加密，且是JSON响应，或者响应中包含encryptedData字段）
  const isJsonResponse = res.headers['content-type'] && res.headers['content-type'].includes('application/json')
  const needDecrypt = encryptConfig.enabled && shouldEncrypt(requestUrl) && isJsonResponse ||
                      (responseData && typeof responseData === 'object' && responseData.encryptedData)

  if (needDecrypt) {
    try {
      // 检查响应是否为加密的字符串（通常是Base64编码的字符串）
      if (typeof responseData === 'string') {
        // 先检查是否是JSON格式的混合加密数据（后端hybridEncryptWithPrivateKey返回的是JSON字符串）
        let isHybridJson = false
        let hybridEncryptedObj = null
        try {
          // 尝试解析为JSON，检查是否是混合加密格式
          hybridEncryptedObj = JSON.parse(responseData)
          if (hybridEncryptedObj && hybridEncryptedObj.encryptedKey && hybridEncryptedObj.encryptedData) {
            isHybridJson = true
          }
        } catch (e) {
          // 不是JSON格式，继续检查其他加密格式
        }

        if (isHybridJson) {
          // 混合加密：后端使用RSA私钥加密，前端使用RSA公钥解密AES密钥，再用AES密钥解密数据
          try {
            // 根据URL获取对应的RSA公钥
            const rsaPublicKey = getRsaPublicKeyByUrl(requestUrl)
            if (!rsaPublicKey) {
              const errorMsg = '响应解密失败：未配置RSA公钥，URL: ' + requestUrl
              throw new Error(errorMsg)
            }

            // 使用 customDecryption.js 中的公钥解密方法进行混合解密
            const decryptedStr = hybridDecryptWithPublicKeyUsingCustomDecryption(hybridEncryptedObj, rsaPublicKey)

            // 尝试解析为JSON（使用大整数安全的解析，保持大整数精度）
            try {
              responseData = parseSafe(decryptedStr)
              // 再次处理大整数，确保所有大整数都保持为字符串
              responseData = convertBigIntToString(responseData)
            } catch (e) {
              // 如果解析失败，使用解密后的字符串
              responseData = decryptedStr
            }
          } catch (error) {
            throw error
          }
        } else {
          // 检查是否是其他加密格式的字符串（通常包含~SLASH~或~SUB~等特殊字符，或者是Base64格式）
          const looksEncrypted = responseData.includes('~SLASH~') || responseData.includes('~SUB~') ||
                                 (responseData.length > 50 && /^[A-Za-z0-9+/=~_-]+$/.test(responseData))

          if (looksEncrypted) {
            // 尝试解密响应数据
            const cryptoType = encryptConfig.cryptoType === 'RSA' ? CryptoType.RSA : CryptoType.AES
            const decryptedStr = decrypt(responseData, cryptoType, encryptConfig.decryptKey)

            // 尝试解析为JSON（使用大整数安全的解析，保持大整数精度）
            try {
              responseData = parseSafe(decryptedStr)
              // 再次处理大整数，确保所有大整数都保持为字符串
              responseData = convertBigIntToString(responseData)
            } catch (e) {
              // 如果解析失败，使用解密后的字符串
              responseData = decryptedStr
            }
          }
        }
      } else if (responseData && typeof responseData === 'object') {
        // 如果响应是对象，检查是否有加密字段
        if (responseData.encryptedData && typeof responseData.encryptedData === 'string') {
          // 如果响应是加密包装对象

          // 先尝试解析 encryptedData 字段，检查是否是混合加密格式
          let encryptedObj
          let isHybridFormat = false
          try {
            encryptedObj = JSON.parse(responseData.encryptedData)
            // 检查是否是混合加密格式（包含 encryptedKey 和 encryptedData）
            if (encryptedObj && encryptedObj.encryptedKey && encryptedObj.encryptedData) {
              isHybridFormat = true
            }
          } catch (e) {
            // 不是JSON格式，继续使用传统解密方式
          }

          let decryptedStr
          if (isHybridFormat) {
            // 混合加密：后端使用RSA私钥加密，前端使用RSA公钥解密AES密钥，再用AES密钥解密数据
            try {
              // 根据URL获取对应的RSA公钥
              const rsaPublicKey = getRsaPublicKeyByUrl(requestUrl)
              if (!rsaPublicKey) {
                const errorMsg = '响应解密失败：未配置RSA公钥，URL: ' + requestUrl
                throw new Error(errorMsg)
              }

              // 使用 customDecryption.js 中的公钥解密方法进行混合解密
              decryptedStr = hybridDecryptWithPublicKeyUsingCustomDecryption(encryptedObj, rsaPublicKey)
            } catch (error) {
              throw error
            }
          } else {
            // 传统加密方式（AES或RSA）
            const cryptoType = encryptConfig.cryptoType === 'RSA' ? CryptoType.RSA : CryptoType.AES
            decryptedStr = decrypt(responseData.encryptedData, cryptoType, encryptConfig.decryptKey)
          }

          try {
            responseData = parseSafe(decryptedStr)
            responseData = convertBigIntToString(responseData)
          } catch (e) {
            // 如果解析失败，创建一个包含错误信息的对象
            responseData = {
              code: 500,
              message: '响应数据解密后无法解析',
              data: decryptedStr
            }
          }
        } else if (responseData.data && typeof responseData.data === 'string') {
          // 检查data字段是否为加密字符串
          const dataStr = responseData.data
          const looksEncrypted = dataStr.includes('~SLASH~') || dataStr.includes('~SUB~') ||
                                 (dataStr.length > 50 && /^[A-Za-z0-9+/=~_-]+$/.test(dataStr))

          if (looksEncrypted) {
            const cryptoType = encryptConfig.cryptoType === 'RSA' ? CryptoType.RSA : CryptoType.AES
            const decryptedStr = decrypt(dataStr, cryptoType, encryptConfig.decryptKey)
            try {
              responseData.data = parseSafe(decryptedStr)
              responseData.data = convertBigIntToString(responseData.data)
            } catch (e) {
              responseData.data = decryptedStr
            }
          }
        } else if (responseData.data && typeof responseData.data === 'object' && responseData.data.encryptedData) {
          // 如果 data 是对象，但包含 encryptedData 字段
          const cryptoType = encryptConfig.cryptoType === 'RSA' ? CryptoType.RSA : CryptoType.AES
          const decryptedStr = decrypt(responseData.data.encryptedData, cryptoType, encryptConfig.decryptKey)
          responseData.data = parseSafe(decryptedStr)
          responseData.data = convertBigIntToString(responseData.data)
        }
      }
    } catch (error) {
      // 解密失败，使用原始数据（不影响接口正常使用）
      responseData = res.data
    }
  }

  // 确保 responseData 是一个对象，如果不是，尝试解析或创建默认对象
  if (typeof responseData !== 'object' || responseData === null) {
    // 如果是字符串，尝试解析为JSON（使用大整数安全解析）
    if (typeof responseData === 'string') {
      try {
        responseData = parseSafe(responseData)
        responseData = convertBigIntToString(responseData)
      } catch (e) {
        // 如果无法解析，创建一个包含错误信息的对象
        responseData = {
          code: 500,
          message: '响应数据格式错误',
          data: responseData
        }
      }
    } else {
      // 其他类型，创建默认对象
      responseData = {
        code: 500,
        message: '响应数据格式错误',
        data: responseData
      }
    }
  }

  // 处理大整数（确保所有大整数都保持为字符串，避免精度丢失）
  // 注意：只在JSON响应时处理，二进制数据不处理
  if (responseData && typeof responseData === 'object' &&
      res.request.responseType !== 'blob' && res.request.responseType !== 'arraybuffer') {
    responseData = convertBigIntToString(responseData)
  }

  return responseData
}
